/**
 * AEO Impact Dashboard
 *
 * Measures whether AEO-enriched product pages produce measurable impact
 * on organic traffic, search rankings, engagement, ad performance,
 * and conversions — compared to non-AEO product pages.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  TrendingUp,
  TrendingDown,
  Eye,
  MousePointer,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BarChart3,
  Users,
  DollarSign,
  Target,
  CheckCircle,
  Clock,
  AlertCircle,
  Package,
  Rocket,
  Gauge,
  Bot,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
} from 'recharts';
import {
  aeoImpactService,
  type AEOImpactData,
  type GSCBucketRow,
  type GA4BucketRow,
} from '@/services/aeoImpactService';

// =========================================================================
// Constants
// =========================================================================

const DATE_RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

const PIE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'];

// =========================================================================
// Helpers
// =========================================================================

const formatNumber = (n: number) => n?.toLocaleString() ?? '0';
const formatPercent = (n: number) => `${(n * 100).toFixed(1)}%`;
const formatPercentRaw = (n: number) => `${n.toFixed(1)}%`;
const formatCurrency = (n: number) =>
  `$${n?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`;
const formatPosition = (n: number) => n?.toFixed(1) ?? '-';
const formatSeconds = (n: number) => {
  if (!n) return '0s';
  if (n < 60) return `${Math.round(n)}s`;
  return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
};

function ChangeIndicator({ value, inverted = false }: { value: number | null | undefined; inverted?: boolean }) {
  if (value === null || value === undefined) return <Minus className="h-4 w-4 text-muted-foreground" />;
  const isPositive = inverted ? value < 0 : value > 0;
  const Icon = isPositive ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const color = isPositive ? 'text-green-500' : value < 0 ? 'text-red-500' : 'text-muted-foreground';
  return (
    <span className={`flex items-center ${color}`}>
      <Icon className="h-4 w-4" />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function MetricCard({
  title,
  value,
  change,
  icon: Icon,
  inverted = false,
}: {
  title: string;
  value: string;
  change?: number | null;
  icon: React.ElementType;
  inverted?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Icon className="h-5 w-5 text-muted-foreground" />
            {change !== undefined && <ChangeIndicator value={change} inverted={inverted} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ComparisonCard({
  title,
  aeoValue,
  nonAeoValue,
  format = 'number',
  inverted = false,
}: {
  title: string;
  aeoValue: number;
  nonAeoValue: number;
  format?: 'number' | 'percent' | 'currency' | 'position' | 'seconds';
  inverted?: boolean;
}) {
  const fmt = (v: number) => {
    switch (format) {
      case 'percent': return formatPercent(v);
      case 'currency': return formatCurrency(v);
      case 'position': return formatPosition(v);
      case 'seconds': return formatSeconds(v);
      default: return formatNumber(v);
    }
  };

  const diff = nonAeoValue !== 0
    ? ((aeoValue - nonAeoValue) / Math.abs(nonAeoValue)) * 100
    : aeoValue > 0 ? 100 : 0;
  const isPositive = inverted ? diff < 0 : diff > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">AEO Pages</p>
            <p className="text-xl font-bold text-blue-600">{fmt(aeoValue)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Non-AEO</p>
            <p className="text-xl font-bold">{fmt(nonAeoValue)}</p>
          </div>
        </div>
        {diff !== 0 && (
          <div className={`mt-2 text-sm flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
            {Math.abs(diff).toFixed(1)}% {isPositive ? 'better' : 'worse'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}><CardContent className="pt-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
        ))}
      </div>
      <Skeleton className="h-[300px] w-full" />
    </div>
  );
}

// =========================================================================
// Main Dashboard
// =========================================================================

export default function AEOImpactDashboard() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const siteId = selectedSite?.id || selectedSite?._id;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AEOImpactData | null>(null);
  const [dateRange, setDateRange] = useState('30');
  const [activeTab, setActiveTab] = useState('organic');

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 86400000).toISOString().split('T')[0];
      const result = await aeoImpactService.getImpactData(siteId, { startDate, endDate });
      setData(result);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [siteId, dateRange, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert><AlertDescription>Select a site to view AEO impact data.</AlertDescription></Alert>
      </div>
    );
  }

  const aeo = (key: string) => data?.gscComparison?.aggregate?.aeo?.[key as keyof typeof data.gscComparison.aggregate.aeo] ?? 0;
  const nonAeo = (key: string) => data?.gscComparison?.aggregate?.non_aeo?.[key as keyof typeof data.gscComparison.aggregate.non_aeo] ?? 0;
  const ga4Aeo = data?.ga4Comparison?.aggregate?.aeo;
  const ga4NonAeo = data?.ga4Comparison?.aggregate?.non_aeo;
  const adsAeo = data?.adsComparison?.aggregate?.aeo;
  const adsNonAeo = data?.adsComparison?.aggregate?.non_aeo;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AEO Impact Dashboard</h1>
          <p className="text-muted-foreground">Compare AEO-enriched vs non-AEO product page performance</p>
        </div>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGES.map(r => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingSkeleton /> : data ? (
        <>
          {/* Summary KPI Cards */}
          <div className="grid grid-cols-4 gap-4">
            <MetricCard
              title="Published Pages"
              value={formatNumber(data.summary.totalPublished)}
              icon={Package}
            />
            <MetricCard
              title="Deployed & Live"
              value={formatNumber(data.summary.totalDeployed)}
              icon={Rocket}
            />
            <MetricCard
              title="Deployment Rate"
              value={formatPercentRaw(data.summary.deploymentRate)}
              icon={CheckCircle}
            />
            <MetricCard
              title="Catalog Coverage"
              value={formatPercentRaw(data.summary.catalogCoverage)}
              icon={Gauge}
            />
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="organic">Organic & Search</TabsTrigger>
              <TabsTrigger value="engagement">Engagement</TabsTrigger>
              <TabsTrigger value="ads">Ads Performance</TabsTrigger>
              <TabsTrigger value="pipeline">Pipeline & AI</TabsTrigger>
            </TabsList>

            {/* Tab 1: Organic & Search */}
            <TabsContent value="organic" className="space-y-6">
              {/* Organic Impact Chart */}
              {data.organicImpact.daily.length > 0 ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Organic Traffic Impact</CardTitle>
                        <CardDescription>Total organic vs deployed-page organic traffic</CardDescription>
                      </div>
                      {data.organicImpact.trend !== null && (
                        <Badge variant={data.organicImpact.trend > 0 ? 'default' : 'destructive'}>
                          <ChangeIndicator value={data.organicImpact.trend} /> 7d trend
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={data.organicImpact.daily}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(d: string) => d.slice(5)} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                        <Tooltip />
                        <Legend />
                        <Bar yAxisId="left" dataKey="totalOrganic" fill="#94a3b8" name="Total Organic" />
                        <Bar yAxisId="left" dataKey="deployedOrganic" fill="#3b82f6" name="Deployed Organic" />
                        <Line yAxisId="right" dataKey="pctOnEnriched" stroke="#f59e0b" name="% on Enriched" dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ) : (
                <Alert><AlertDescription>No organic traffic data available for this period.</AlertDescription></Alert>
              )}

              {/* GSC Comparison Cards */}
              {data.gscComparison.aggregate.aeo ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">AEO Pages ({aeo('products')} products)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-muted-foreground">Impressions</span><p className="font-bold">{formatNumber(aeo('totalImpressions') as number)}</p></div>
                          <div><span className="text-muted-foreground">Clicks</span><p className="font-bold">{formatNumber(aeo('totalClicks') as number)}</p></div>
                          <div><span className="text-muted-foreground">CTR</span><p className="font-bold">{formatPercent(aeo('avgCtr') as number)}</p></div>
                          <div><span className="text-muted-foreground">Avg Position</span><p className="font-bold">{formatPosition(aeo('avgPosition') as number)}</p></div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Non-AEO Pages ({nonAeo('products')} products)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-muted-foreground">Impressions</span><p className="font-bold">{formatNumber(nonAeo('totalImpressions') as number)}</p></div>
                          <div><span className="text-muted-foreground">Clicks</span><p className="font-bold">{formatNumber(nonAeo('totalClicks') as number)}</p></div>
                          <div><span className="text-muted-foreground">CTR</span><p className="font-bold">{formatPercent(nonAeo('avgCtr') as number)}</p></div>
                          <div><span className="text-muted-foreground">Avg Position</span><p className="font-bold">{formatPosition(nonAeo('avgPosition') as number)}</p></div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Traffic Bucket Table */}
                  {data.gscComparison.buckets.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>GSC Performance by Traffic Bucket</CardTitle>
                        <CardDescription>Fair comparison within same impression tiers</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Bucket</TableHead>
                              <TableHead>Group</TableHead>
                              <TableHead className="text-right">Products</TableHead>
                              <TableHead className="text-right">Impressions</TableHead>
                              <TableHead className="text-right">Clicks</TableHead>
                              <TableHead className="text-right">CTR</TableHead>
                              <TableHead className="text-right">Avg Position</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.gscComparison.buckets.map((row: GSCBucketRow, i: number) => (
                              <TableRow key={i} className={row.group === 'aeo' ? 'bg-blue-50/50' : ''}>
                                <TableCell>{row.bucket}</TableCell>
                                <TableCell>
                                  <Badge variant={row.group === 'aeo' ? 'default' : 'secondary'}>
                                    {row.group === 'aeo' ? 'AEO' : 'Non-AEO'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">{formatNumber(row.products)}</TableCell>
                                <TableCell className="text-right">{formatNumber(row.totalImpressions)}</TableCell>
                                <TableCell className="text-right">{formatNumber(row.totalClicks)}</TableCell>
                                <TableCell className="text-right">{formatPercent(row.avgCtr)}</TableCell>
                                <TableCell className="text-right">{formatPosition(row.avgPosition)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Alert><AlertDescription>No GSC data available for product pages in this period.</AlertDescription></Alert>
              )}
            </TabsContent>

            {/* Tab 2: Engagement */}
            <TabsContent value="engagement" className="space-y-6">
              {ga4Aeo ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <ComparisonCard
                      title="Bounce Rate"
                      aeoValue={ga4Aeo.avgBounceRate}
                      nonAeoValue={ga4NonAeo?.avgBounceRate ?? 0}
                      format="percent"
                      inverted
                    />
                    <ComparisonCard
                      title="Engagement Rate"
                      aeoValue={ga4Aeo.avgEngagementRate}
                      nonAeoValue={ga4NonAeo?.avgEngagementRate ?? 0}
                      format="percent"
                    />
                    <ComparisonCard
                      title="Time on Page"
                      aeoValue={ga4Aeo.avgTimeOnPage}
                      nonAeoValue={ga4NonAeo?.avgTimeOnPage ?? 0}
                      format="seconds"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <ComparisonCard
                      title="Conversions"
                      aeoValue={ga4Aeo.totalConversions}
                      nonAeoValue={ga4NonAeo?.totalConversions ?? 0}
                    />
                    <ComparisonCard
                      title="Revenue per Session"
                      aeoValue={ga4Aeo.revenuePerSession}
                      nonAeoValue={ga4NonAeo?.revenuePerSession ?? 0}
                      format="currency"
                    />
                    <ComparisonCard
                      title="Total Sessions"
                      aeoValue={ga4Aeo.totalSessions}
                      nonAeoValue={ga4NonAeo?.totalSessions ?? 0}
                    />
                  </div>

                  {/* GA4 Bucket Table */}
                  {data.ga4Comparison.buckets.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>GA4 Engagement by Session Bucket</CardTitle>
                        <CardDescription>Fair comparison within same session volume tiers</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Bucket</TableHead>
                              <TableHead>Group</TableHead>
                              <TableHead className="text-right">Products</TableHead>
                              <TableHead className="text-right">Sessions</TableHead>
                              <TableHead className="text-right">Bounce Rate</TableHead>
                              <TableHead className="text-right">Engagement</TableHead>
                              <TableHead className="text-right">Avg Time</TableHead>
                              <TableHead className="text-right">Conversions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.ga4Comparison.buckets.map((row: GA4BucketRow, i: number) => (
                              <TableRow key={i} className={row.group === 'aeo' ? 'bg-blue-50/50' : ''}>
                                <TableCell>{row.bucket}</TableCell>
                                <TableCell>
                                  <Badge variant={row.group === 'aeo' ? 'default' : 'secondary'}>
                                    {row.group === 'aeo' ? 'AEO' : 'Non-AEO'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">{formatNumber(row.products)}</TableCell>
                                <TableCell className="text-right">{formatNumber(row.totalSessions)}</TableCell>
                                <TableCell className="text-right">{formatPercent(row.avgBounceRate)}</TableCell>
                                <TableCell className="text-right">{formatPercent(row.avgEngagementRate)}</TableCell>
                                <TableCell className="text-right">{formatSeconds(row.avgTimeOnPage)}</TableCell>
                                <TableCell className="text-right">{formatNumber(row.totalConversions)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Alert><AlertDescription>No GA4 engagement data available for product pages in this period.</AlertDescription></Alert>
              )}
            </TabsContent>

            {/* Tab 3: Ads Performance */}
            <TabsContent value="ads" className="space-y-6">
              {adsAeo ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <ComparisonCard
                      title="Conversion Rate"
                      aeoValue={adsAeo.conversionRate}
                      nonAeoValue={adsNonAeo?.conversionRate ?? 0}
                      format="percent"
                    />
                    <ComparisonCard
                      title="Avg CPC"
                      aeoValue={adsAeo.avgCpc}
                      nonAeoValue={adsNonAeo?.avgCpc ?? 0}
                      format="currency"
                      inverted
                    />
                    <ComparisonCard
                      title="Total Conversions"
                      aeoValue={adsAeo.totalConversions}
                      nonAeoValue={adsNonAeo?.totalConversions ?? 0}
                    />
                  </div>

                  {/* Quality Score Distribution */}
                  {data.adsComparison.qualityDistribution && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Landing Page Quality Distribution</CardTitle>
                        <CardDescription>Google Ads landing page experience scores</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-6">
                          {['aeo', 'non_aeo'].map(grp => {
                            const dist = data.adsComparison.qualityDistribution[grp] || {};
                            const chartData = Object.entries(dist).map(([name, value]) => ({ name, value }));
                            if (chartData.length === 0) return null;
                            return (
                              <div key={grp}>
                                <p className="text-sm font-medium mb-2">{grp === 'aeo' ? 'AEO Pages' : 'Non-AEO Pages'}</p>
                                <ResponsiveContainer width="100%" height={200}>
                                  <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill={grp === 'aeo' ? '#3b82f6' : '#94a3b8'} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Alert><AlertDescription>No Google Ads data available for product pages in this period.</AlertDescription></Alert>
              )}
            </TabsContent>

            {/* Tab 4: Pipeline & AI */}
            <TabsContent value="pipeline" className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Deployment Status Pie */}
                <Card>
                  <CardHeader>
                    <CardTitle>Deployment Status</CardTitle>
                    <CardDescription>Distribution of AEO content deployment stages</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {data.deploymentStatus && Object.keys(data.deploymentStatus).length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(data.deploymentStatus).map(([name, value]) => ({ name, value }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, value }: { name: string; value: number }) => `${name}: ${value}`}
                          >
                            {Object.keys(data.deploymentStatus).map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-muted-foreground text-sm">No deployment data available.</p>
                    )}
                  </CardContent>
                </Card>

                {/* AI Visibility & Coverage */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Bot className="h-5 w-5" />
                        AI Visibility
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{data.aiVisibility.message}</AlertDescription>
                      </Alert>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Content Coverage</CardTitle>
                      <CardDescription>
                        {formatNumber(data.summary.totalPublished)} of {formatNumber(data.summary.totalCatalog)} catalog products
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Coverage</span>
                          <span className="font-medium">{formatPercentRaw(data.summary.catalogCoverage)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full transition-all"
                            style={{ width: `${Math.min(data.summary.catalogCoverage, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Deployment</span>
                          <span className="font-medium">{formatPercentRaw(data.summary.deploymentRate)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-green-600 h-3 rounded-full transition-all"
                            style={{ width: `${Math.min(data.summary.deploymentRate, 100)}%` }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </>
      ) : null}
    </div>
  );
}
